import {
  ArrowRightIcon,
  ArrowTopRightOnSquareIcon,
  BuildingStorefrontIcon,
  BookOpenIcon,
  MapIcon,
  Squares2X2Icon,
  StarIcon,
} from "@heroicons/react/24/outline";
import React from "react";
import styled, { css } from "styled-components";

import { UnstyledLink } from "src/shared/components/Link";
import { Route } from "src/shared/constants/route";
import { SlackIcon } from "src/static/icons";
import { DevpostLogoImg, MuseumLampImg } from "src/static/img";

const DashboardResources: React.FC = () => {
  return (
    <ResourceGrid>
      <ResourceCard>
        <ResourceHeading>
          <SectionTitle>Things to do</SectionTitle>
          <SectionDescription>
            Connect, compete, and collaborate—pick an activity below to get
            started.
          </SectionDescription>
        </ResourceHeading>
        <ActionList>
          <ActionLink href={Route.HACKER_MENTOR_CAFE}>
            <ActionIdentity>
              <BuildingStorefrontIcon aria-hidden="true" />
              <span>Find a mentor</span>
            </ActionIdentity>
            <ArrowRightIcon aria-hidden="true" />
          </ActionLink>
          <ActionLink href={Route.GOOSE_GAMES_HOME}>
            <ActionIdentity>
              <StarIcon aria-hidden="true" />
              <span>Earn Goose Game Points</span>
            </ActionIdentity>
            <ArrowRightIcon aria-hidden="true" />
          </ActionLink>
          <ActionLink href={Route.HACKER_DIRECTORY_DIRECTORY}>
            <ActionIdentity>
              <Squares2X2Icon aria-hidden="true" />
              <span>Find Teammates</span>
            </ActionIdentity>
            <ArrowRightIcon aria-hidden="true" />
          </ActionLink>
        </ActionList>
      </ResourceCard>
      <ResourceCard>
        <ResourceHeading>
          <SectionTitle>Quick Links</SectionTitle>
          <SectionDescription>
            Key resources, guides, and tools to navigate your hackathon
            experience.
          </SectionDescription>
        </ResourceHeading>
        <QuickLinkList>
          <QuickLink
            href="https://hackthenorth.slack.com/"
            newTab
            rel="noopener noreferrer"
          >
            <QuickLinkIdentity>
              <SlackIcon aria-hidden="true" />
              <span>Slack</span>
            </QuickLinkIdentity>
            <ArrowTopRightOnSquareIcon aria-hidden="true" />
          </QuickLink>
          <QuickLink
            href="https://hackthenorth2026.devpost.com/"
            newTab
            rel="noopener noreferrer"
          >
            <QuickLinkIdentity>
              <img src={DevpostLogoImg} alt="" aria-hidden="true" />
              <span>Devpost</span>
            </QuickLinkIdentity>
            <ArrowTopRightOnSquareIcon aria-hidden="true" />
          </QuickLink>
          <QuickLink
            href="https://museum.hackthenorth.com/"
            newTab
            rel="noopener noreferrer"
          >
            <QuickLinkIdentity>
              <MuseumLampSlot aria-hidden="true">
                <img src={MuseumLampImg} alt="" />
              </MuseumLampSlot>
              <span>Museum of Hack the North</span>
            </QuickLinkIdentity>
            <ArrowTopRightOnSquareIcon aria-hidden="true" />
          </QuickLink>
          <QuickLink
            href="https://hackthenorth.notion.site/2026-welcome-booklet"
            newTab
            rel="noopener noreferrer"
          >
            <QuickLinkIdentity>
              <BookOpenIcon aria-hidden="true" />
              <span>Welcome Booklet</span>
            </QuickLinkIdentity>
            <ArrowTopRightOnSquareIcon aria-hidden="true" />
          </QuickLink>
          <QuickLink
            href="https://hackthenorth.notion.site/Event-Maps-3d4b70e03de78098add9c017fe79a7cb"
            newTab
            rel="noopener noreferrer"
          >
            <QuickLinkIdentity>
              <MapIcon aria-hidden="true" />
              <span>Venue Map</span>
            </QuickLinkIdentity>
            <ArrowTopRightOnSquareIcon aria-hidden="true" />
          </QuickLink>
        </QuickLinkList>
      </ResourceCard>
    </ResourceGrid>
  );
};

export default DashboardResources;

const ResourceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  align-items: start;
  gap: 24px;

  ${({ theme }) => theme.mediaQueries.medium`
    grid-template-columns: 1fr;
  `}

  ${({ theme }) => theme.mediaQueries.tablet`
    gap: 32px;
  `}
`;

const ResourceCard = styled.section`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
  padding: 16px;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.globalConstants.color.borderGray};
  border-radius: 8px;
  background: ${({ theme }) => theme.globalConstants.color.white};

  ${({ theme }) => theme.mediaQueries.tablet`
    padding: 16px 0;
    border: 0;
    border-radius: 0;
  `}
`;

const ResourceHeading = styled.div`
  display: flex;
  flex-direction: column;
`;

const SectionTitle = styled.h2`
  margin: 0;
  color: ${({ theme }) => theme.globalConstants.color.textSecondary};
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.body};
  font-size: 16px;
  font-weight: 700;
  line-height: 1.5;
  letter-spacing: 0.8px;
  text-transform: uppercase;
`;

const SectionDescription = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.globalConstants.color.textBody};
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.body};
  font-size: 16px;
  font-weight: 400;
  line-height: 1.6;
`;

const ActionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ActionLink = styled(UnstyledLink)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  min-height: 64px;
  padding: 16px;
  border: 1px solid ${({ theme }) => theme.globalConstants.color.borderGray};
  border-radius: 8px;
  background: ${({ theme }) => theme.globalConstants.color.white};
  color: ${({ theme }) => theme.globalConstants.color.textBody};
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.body};
  font-size: 16px;
  font-weight: 700;
  line-height: 1.6;
  text-decoration: none;

  > svg {
    width: 20px;
    height: 20px;
    flex: 0 0 20px;
    color: ${({ theme }) => theme.globalConstants.color.textTertiary};
  }

  &:hover,
  &:focus {
    border-color: ${({ theme }) => theme.globalConstants.color.brandPrimary};
    color: ${({ theme }) => theme.globalConstants.color.textBody};
    text-decoration: none;
  }
`;

const ActionIdentity = styled.span`
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 0;

  svg {
    width: 32px;
    height: 32px;
    flex: 0 0 32px;
    color: ${({ theme }) => theme.globalConstants.color.brandPrimary};
  }
`;

const QuickLinkList = styled.div`
  display: flex;
  flex-direction: column;
`;

const quickLinkStyles = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  min-height: 58px;
  padding: 16px 0;
  color: ${({ theme }) => theme.globalConstants.color.textSecondary};
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.body};
  font-size: 16px;
  font-weight: 700;
  line-height: 1.6;
  text-decoration: none;

  > svg {
    width: 24px;
    height: 24px;
    flex: 0 0 24px;
    color: ${({ theme }) => theme.globalConstants.color.textTertiary};
  }
`;

const QuickLink = styled(UnstyledLink)`
  ${quickLinkStyles}

  &:hover,
  &:focus {
    color: ${({ theme }) => theme.globalConstants.color.brandPrimary};
    text-decoration: none;
  }
`;

const QuickLinkIdentity = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;

  > svg,
  > img {
    width: 24px;
    height: 24px;
    flex: 0 0 24px;
    object-fit: contain;
  }

  > span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const MuseumLampSlot = styled.span`
  display: flex;
  align-items: center;
  width: 24px;
  height: 24px;
  flex: 0 0 24px;

  img {
    display: block;
    width: 24px;
    height: 13.897px;
  }
`;
